import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

//========== Import components from MUI Library ============
//All Wrapper Elements
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import Stack from "@mui/material/Stack";
import Paper from "@mui/material/Paper";
//All Loading related components
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
//All Element/ Controls
import Typography from "@mui/material/Typography";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
// All Icons
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

//========== Import components from our files ============
// All Common Element/ Controls
import { ButtonUI, KMKDrawer, PermanentDeletePopup } from "../Common";
import { setPrevState } from "../../util/common";
import { showToast } from "../../store/actions";
import { regexName } from "../../util/regularExpress";
import ComponentSuspense from "../Common/ComponentSuspense";
// Others
import WorkspaceClass from "../../service/workspace";
import { setWorkspace } from "../../store/actions/workspace";
import { checkReservedKeywords } from "../../util/restrictedStatement";
const KMKTextEditor = React.lazy(() => import("../Common/KMKTextEditor"));

const DeleteConfirm = ({ workspaceName, handleCancel, deleteWorkspace, loadingForConfirm }) => {
  return (
    <PermanentDeletePopup
      item={"Workspace"}
      title={
        <>
          <span style={{ fontWeight: "bold" }}>{workspaceName} </span>
          will be permanently removed. This will also remove all the data stored in the data warehouse tables associated
          with this workspace.
        </>
      }
      message={`Are you sure you want to delete "${workspaceName}" Workspace? You can recover the workspace within 30 days after contacting the system administrator.`}
      handleDelete={deleteWorkspace}
      handleCancel={handleCancel}
      loadingForConfirm={loadingForConfirm}
    />
  );
};

const WorkspaceSettings = ({ setOpen }) => {
  const { Workspace_id } = useSelector((state) => state?.workspace?.workspace);

  const [loading, setLoading] = useState(false);
  const [loadingForConfirm, setLoadingForConfirm] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [userList, setUserList] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [dbError, setDBError] = useState("");
  const [settingState, setSettingState] = useState({
    workspaceName: "",
    workspaceType: 1,
    databaseName: "",
    owner: {},
    workspaceMessage: "",
  });
  const [oldSettingState, setOldSettingState] = useState({
    workspaceName: "",
    workspaceType: 1,
    databaseName: "",
    owner: {},
    workspaceMessage: "",
  });

  const workspaceMessageRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const user = useSelector((state) => state.user);

  const handleWorkspaceName = (workspaceName) => {
    const isValid = regexName(workspaceName, "alphabetStart");
    if (isValid) setPrevState(setSettingState, { workspaceName: workspaceName });
  };

  const handleDatabaseName = (DBName) => {
    const isValid = regexName(DBName, "alphaWithUnderscore");

    if (isValid) {
      setPrevState(setSettingState, { databaseName: DBName });

      if (DBName.length && DBName.length < 2) setDBError("Database name must be 2-50 characters long");
      else if (checkReservedKeywords(DBName)) setDBError("Reserved keyword.");
      else setDBError("");
    }
  };

  const handleTypeChange = (e) => {
    setPrevState(setSettingState, { workspaceType: e.target.value });
    if (e.target.value === 2) {
      const filter = userList.filter((user) => !user.hasPersonalWorkspace);
      setFilteredList(filter);
    } else setFilteredList(userList);
  };

  const handleSave = async () => {
    setLoading(true);
    const submitData = {
      name: settingState.workspaceName,
      dbName: settingState.databaseName.toUpperCase(),
      type: settingState.workspaceType,
      ownerId: settingState.owner.User_id,
      Message: workspaceMessageRef.current.getEditor().getText().trim().length > 0 ? settingState.workspaceMessage : "",
    };
    const workspace = new WorkspaceClass();
    const result = await workspace.updateWorkspaceSettings(Workspace_id, submitData);

    if (result.success) {
      dispatch(
        showToast({
          message: result.message,
          type: "success",
        })
      );

      //Fetching new details again
      const { data, success } = await workspace.getWorkspaceDetail(Workspace_id);
      if (success) dispatch(setWorkspace(data));

      setOpen(false);
    }

    setLoading(false);
  };

  const handleCancel = () => {
    setOpenConfirm(false);
  };

  useEffect(() => {
    const loadSettings = async () => {
      setLoading(true);
      const workspace = new WorkspaceClass();
      const result = await workspace.getWorkspaceSettings(Workspace_id);
      if (result) {
        setUserList(result.adminUserList);
        setFilteredList(result.adminUserList);
        setSettingState({
          workspaceName: result.workspace.Name,
          workspaceType: result.workspace.Type,
          databaseName: result.workspace.DBName.toUpperCase(),
          workspaceMessage: result.workspace.Message,
          owner: {
            User_id: result.workspace.Owner_id,
            UserName: result.workspace.Owner,
          },
        });
        setOldSettingState({
          workspaceName: result.workspace.Name,
          workspaceType: result.workspace.Type,
          databaseName: result.workspace.DBName.toUpperCase(),
          workspaceMessage: result.workspace.Message,
          owner: {
            User_id: result.workspace.Owner_id,
            UserName: result.workspace.Owner,
          },
        });
      }
      setLoading(false);
    };
    loadSettings();
  }, [user.currentUser.User_id, Workspace_id]);

  const deleteWorkspace = async () => {
    setLoadingForConfirm(true);

    const workspace = new WorkspaceClass();
    const result = await workspace.deleteWorkspace(Workspace_id);

    if (result.success) {
      dispatch(
        showToast({
          message: result.message,
          type: "success",
        })
      );
      setOpenConfirm(false);
      navigate("/my-workspaces");
    }
    setLoadingForConfirm(false);
  };

  const { connections } = useSelector((state) => state.workspace);

  const $connections = useMemo(
    () =>
      connections
        .filter((item) => item.Type === "Snowflake" || item.Type === "Tableau" || item.Type === "PowerBI")
        .map((item) => {
          let img = "";
          if (item.Type === "Snowflake") img = "snowflake-blue.png";
          else if (item.Type === "Tableau") img = "tableua.png";
          else img = "spinner-PBI-logo.svg";

          const imgSrc = `/images/${img}`;

          return {
            imgSrc,
            accessUrl: item?.Detail?.accessUrl,
            Type: item.Type,
          };
        }),
    [connections]
  );

  return (
    <KMKDrawer anchor="right" open={true} width={250}>
      <Toolbar />
      <Paper
        elevation={0}
        sx={{
          position: "sticky",
          right: 12,
          top: 70,
          borderRadius: "0px",
          backgrounColor: "black",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          display: "flex",
          alignItems: "baseline",
        }}
      >
        <Typography
          sx={{
            flexGrow: 1,
            paddingLeft: "18px",
          }}
          color="primary"
        >
          Workspace Settings
        </Typography>
        <IconButton aria-label="close" onClick={() => setOpen(false)}>
          <CloseIcon />
        </IconButton>
      </Paper>
      <Box
        sx={{
          width: { xs: "100%", sm: "500px" },
          height: "100%",
          padding: "25px 20px",
        }}
        role="presentation"
      >
        <Backdrop
          sx={{
            backgroundColor: "transparent",
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
          open={loading}
        >
          <CircularProgress color="primary" />
        </Backdrop>
        <div style={{ display: "flex", flexDirection: "column" }}>
          {$connections.map((connection) => (
            <div
              key={connection.key}
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                marginBottom: "10px",
              }}
            >
              <img
                src={connection.imgSrc}
                alt={connection.Type}
                style={{ width: "30px", height: "30px", marginRight: "5px" }}
              />
              <Typography variant="body2" style={{ fontWeight: "bold", fontSize: "12px" }}>
                {connection.Type}:
              </Typography>
              <a
                href={connection.accessUrl}
                target="_blank"
                rel="noreferrer"
                style={{
                  whiteSpace: "nowrap",
                  marginLeft: "10px",
                  fontSize: "13px",
                  textDecoration: "none",
                  color: "#0AB2B2",
                }}
              >
                {connection.accessUrl}
              </a>
            </div>
          ))}
        </div>
        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField
              value={settingState.workspaceName}
              margin="normal"
              onChange={(e) => handleWorkspaceName(e.target.value)}
              inputProps={{ maxLength: 50 }}
              required
              fullWidth
              label="Workspace Name"
            />
          </Grid>
          <Grid size={12}>
            <TextField
              value={settingState.databaseName.toUpperCase()}
              margin="dense"
              onChange={(e) => handleDatabaseName(e.target.value)}
              inputProps={{ maxLength: 50 }}
              required
              fullWidth
              label="Database Name"
              error={!!dbError}
              helperText={dbError || "Make sure to update all references to the DB name in this workspace."}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              margin="dense"
              fullWidth
              label="Type"
              variant="outlined"
              value={settingState.workspaceType}
              onChange={(e) => handleTypeChange(e)}
              required
              select
            >
              <MenuItem value={1}>Project</MenuItem>
              <MenuItem value={2}>Personal</MenuItem>
            </TextField>
          </Grid>
          <Grid size={12}>
            <Autocomplete
              isOptionEqualToValue={(option, value) => option.User_id === value.User_id}
              fullWidth
              options={filteredList}
              id="checkboxes-tags-demo"
              getOptionLabel={(option) => {
                return option.UserName || "";
              }}
              value={settingState.owner}
              renderOption={(props, option) => (
                <li {...props} key={option.User_id}>
                  {option.UserName}
                </li>
              )}
              renderInput={(params) => (
                <TextField required variant="outlined" {...params} label="Owner" placeholder="Owner" />
              )}
              onChange={(e, selectedValues) => setPrevState(setSettingState, { owner: selectedValues })}
              ListboxProps={{
                style: {
                  maxHeight: "185px",
                },
              }}
            />
          </Grid>
          <Grid sx={{ marginBottom: "75px" }} size={12}>
            <Typography
              sx={{
                fontWeight: "bolder",
                fontSize: "1rem",
                paddingBottom: "7px",
              }}
            >
              Workspace message :
            </Typography>
            <ComponentSuspense>
              <KMKTextEditor
                editorRef={workspaceMessageRef}
                placeholder="Workspace message"
                value={settingState.workspaceMessage}
                setValue={(content) => {
                  setPrevState(setSettingState, { workspaceMessage: content });
                }}
                style={{
                  height: "200px",
                  width: "100%",
                }}
                theme="snow"
              />
            </ComponentSuspense>
          </Grid>
          <Grid container direction="row" sx={{ marginTop: "20px" }}>
            <Grid
              size={{
                xs: 6,
                sm: 6
              }}>
              <Stack spacing={2} direction="row" sx={{ mb: "10px" }}>
                <ButtonUI
                  style={{ marginLeft: "15px", width: "auto" }}
                  variant="contained"
                  color="error"
                  onClick={() => setOpenConfirm(true)}
                >
                  Delete Workspace
                </ButtonUI>
              </Stack>
            </Grid>
            <Grid
              size={{
                xs: 6,
                sm: 6
              }}>
              <Stack spacing={2} direction="row" sx={{ mb: "10px" }} justifyContent="flex-end">
                <ButtonUI
                  variant="contained"
                  color="success"
                  style={{ width: "80px" }}
                  onClick={() => handleSave()}
                  disabled={
                    !settingState.workspaceName ||
                    !settingState.databaseName ||
                    dbError ||
                    !settingState.workspaceType ||
                    !settingState.owner ||
                    (oldSettingState.workspaceName === settingState.workspaceName &&
                      oldSettingState.databaseName.toUpperCase() === settingState.databaseName.toUpperCase() &&
                      oldSettingState.workspaceType === settingState.workspaceType &&
                      oldSettingState.owner.User_id === settingState.owner.User_id &&
                      (oldSettingState.workspaceMessage?.length > 0
                        ? oldSettingState.workspaceMessage === settingState.workspaceMessage
                        : workspaceMessageRef?.current?.getEditor().getText().trim().length === 0))
                  }
                >
                  Save
                </ButtonUI>
                <ButtonUI
                  variant="contained"
                  color="secondary"
                  style={{
                    width: "80px",
                    marginLeft: 15,
                    backgroundColor: "#3a3d4a",
                  }}
                  onClick={() => setOpen(false)}
                >
                  Cancel
                </ButtonUI>
              </Stack>
            </Grid>
          </Grid>
        </Grid>
      </Box>
      {openConfirm && (
        <DeleteConfirm
          workspaceName={oldSettingState.workspaceName}
          handleCancel={handleCancel}
          deleteWorkspace={deleteWorkspace}
          loadingForConfirm={loadingForConfirm}
        />
      )}
    </KMKDrawer>
  );
};

export default WorkspaceSettings;
